import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppShell } from '@/layouts/AppShell';
import { ProtectedRoute } from '@/lib/ProtectedRoute';
import { Spinner } from '@/components/ui/Spinner';

const LoginPage = lazy(() => import('@/features/auth/LoginPage'));
const DashboardPage = lazy(() => import('@/features/dashboard/DashboardPage'));
const SubscribersPage = lazy(() => import('@/features/subscribers/SubscribersPage'));
const SubscriberDetailPage = lazy(() => import('@/features/subscribers/SubscriberDetailPage'));
const MetersPage = lazy(() => import('@/features/meters/MetersPage'));
const ReadingsPage = lazy(() => import('@/features/readings/ReadingsPage'));
const BulkEntryPage = lazy(() => import('@/features/readings/BulkEntryPage'));
const BillingRunsPage = lazy(() => import('@/features/billing/BillingRunsPage'));
const DirectoriesIndexPage = lazy(() => import('@/features/reference/DirectoriesIndexPage'));
const DirectoryPage = lazy(() => import('@/features/reference/DirectoryPage'));
const TariffsPage = lazy(() => import('@/features/reference/TariffsPage'));
const ExecutiveDashboard = lazy(() => import('@/features/reports/ExecutiveDashboard'));
const WaterBalancePage = lazy(() => import('@/features/reports/WaterBalancePage'));
const CostsPage = lazy(() => import('@/features/reports/CostsPage'));
const SourcesPage = lazy(() => import('@/features/source/SourcesPage'));
const TicketsPage = lazy(() => import('@/features/dispatch/TicketsPage'));
const MapPage = lazy(() => import('@/features/infrastructure/MapPage'));
const CashPage = lazy(() => import('@/features/cash/CashPage'));
const DebtPage = lazy(() => import('@/features/debt/DebtPage'));
const UsersPage = lazy(() => import('@/features/admin/UsersPage'));
const RolesPage = lazy(() => import('@/features/admin/RolesPage'));

function Loading(): React.JSX.Element {
  return (
    <div className="flex min-h-64 items-center justify-center">
      <Spinner size="lg" label="Загрузка раздела" />
    </div>
  );
}

function lazyPage(node: React.ReactNode): React.JSX.Element {
  return <Suspense fallback={<Loading />}>{node}</Suspense>;
}

export const router = createBrowserRouter([
  { path: '/login', element: lazyPage(<LoginPage />) },
  {
    path: '/',
    element: <ProtectedRoute />,
    children: [
      {
        element: <AppShell />,
        children: [
          { index: true, element: <Navigate to="/dashboard" replace /> },
          { path: 'dashboard', element: lazyPage(<DashboardPage />) },
          {
            path: 'subscribers',
            element: <ProtectedRoute permissions={['subscribers.read']} />,
            children: [
              { index: true, element: lazyPage(<SubscribersPage />) },
              { path: ':id', element: lazyPage(<SubscriberDetailPage />) },
            ],
          },
          {
            path: 'meters',
            element: <ProtectedRoute permissions={['meters.read']} />,
            children: [{ index: true, element: lazyPage(<MetersPage />) }],
          },
          {
            path: 'readings',
            element: <ProtectedRoute permissions={['readings.read']} />,
            children: [{ index: true, element: lazyPage(<ReadingsPage />) }],
          },
          {
            path: 'readings/entry',
            element: <ProtectedRoute permissions={['readings.write']} />,
            children: [{ index: true, element: lazyPage(<BulkEntryPage />) }],
          },
          {
            path: 'billing',
            element: <ProtectedRoute permissions={['billing.read']} />,
            children: [{ index: true, element: lazyPage(<BillingRunsPage />) }],
          },
          {
            path: 'reports',
            element: <ProtectedRoute permissions={['reports.read']} />,
            children: [{ index: true, element: lazyPage(<ExecutiveDashboard />) }],
          },
          {
            path: 'reports/balance',
            element: <ProtectedRoute permissions={['reports.balance', 'reports.read']} />,
            children: [{ index: true, element: lazyPage(<WaterBalancePage />) }],
          },
          {
            path: 'reports/costs',
            element: <ProtectedRoute permissions={['reports.read']} />,
            children: [{ index: true, element: lazyPage(<CostsPage />) }],
          },
          {
            path: 'source',
            element: <ProtectedRoute permissions={['infrastructure.read']} />,
            children: [{ index: true, element: lazyPage(<SourcesPage />) }],
          },
          {
            path: 'dispatch',
            element: <ProtectedRoute permissions={['dispatch.read']} />,
            children: [{ index: true, element: lazyPage(<TicketsPage />) }],
          },
          {
            path: 'infrastructure/map',
            element: <ProtectedRoute permissions={['infrastructure.read']} />,
            children: [{ index: true, element: lazyPage(<MapPage />) }],
          },
          {
            path: 'cash',
            element: <ProtectedRoute permissions={['payments.read']} />,
            children: [{ index: true, element: lazyPage(<CashPage />) }],
          },
          {
            path: 'debt',
            element: <ProtectedRoute permissions={['debt.read']} />,
            children: [{ index: true, element: lazyPage(<DebtPage />) }],
          },
          {
            path: 'admin/users',
            element: <ProtectedRoute permissions={['admin.users.read']} />,
            children: [{ index: true, element: lazyPage(<UsersPage />) }],
          },
          {
            path: 'admin/roles',
            element: <ProtectedRoute permissions={['admin.read']} />,
            children: [{ index: true, element: lazyPage(<RolesPage />) }],
          },
          {
            path: 'reference',
            children: [
              { index: true, element: lazyPage(<DirectoriesIndexPage />) },
              { path: ':key', element: lazyPage(<DirectoryPage />) },
            ],
          },
          {
            path: 'tariffs',
            element: <ProtectedRoute permissions={['tariffs.read']} />,
            children: [{ index: true, element: lazyPage(<TariffsPage />) }],
          },
        ],
      },
    ],
  },
  { path: '*', element: <Navigate to="/dashboard" replace /> },
]);
